import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, model, signal } from '@angular/core';
import { catchError, finalize, Observable, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { FilesComponent } from '../files/files.component';
import { isValidFile, SelectedFile } from '../selected-file.model';
import { escape } from '../string-helper';
import { StudiesComponent } from '../studies/studies.component';
import { Study, StudyService } from '../study.service';
import { UploadTagsComponent } from '../upload-tags/upload-tags.component';
import { Upload, UploadService } from '../upload.service';

/**
 * Data upload form component.
 */
@Component({
  selector: 'app-upload-form',
  templateUrl: './upload-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AsyncPipe, FilesComponent, StudiesComponent, UploadTagsComponent]
})
export class UploadFormComponent {
  private readonly studyService = inject(StudyService);
  private readonly uploadService = inject(UploadService);

  /**
  * Selected files.
  */
  public files = model.required<SelectedFile[]>();

  /**
  * Available studies list.
  */
  protected studies$: Observable<Study[]> = this.studyService.get();

  /**
   * Selected study.
   */
  public selectedStudy = model<Study>();

  /**
   * Accepted file types.
   */
  protected readonly fileTypes = computed(() => this.selectedStudy()?.fileTypes ?? []);

  /**
   * Data upload.
   */
  public entity = model<Upload>();

  protected readonly disabled = signal(true);
  protected readonly loading = signal(true);

  constructor() {
    this.uploadService.blank().pipe(
      tap(entity => {
        // upload info
        entity.uploadInfo.collection = environment.dl.collection;
        // file prep
        entity.filePreparation.dataPreparation.answer = false;
        // set validation defaults if not provided
        if (!entity.uploadInfo.namePattern)
          entity.uploadInfo.namePattern = String.raw`^[A-Za-z0-9 \-&_]*$`;
        if (!entity.uploadInfo.nameMaxLength)
          entity.uploadInfo.nameMaxLength = 50;
        if (!entity.uploadInfo.commentMaxLength)
          entity.uploadInfo.commentMaxLength = 200;
        if (!entity.tags.nameMaxLength)
          entity.tags.nameMaxLength = 50;
        if (!entity.tags.namePattern)
          entity.tags.namePattern = '^[A-Za-z][A-Za-z0-9]*$';
        if (!entity.tags.valueMaxLength)
          entity.tags.valueMaxLength = 50;
        if (!entity.tags.valuePattern)
          entity.tags.valuePattern = '^[A-Za-z0-9 -]*$';
        // update property
        this.onSuccessResponse(entity)
        this.disabled.set(false);
      }),
      finalize(() => this.loading.set(false))
    ).subscribe();
  }

  /**
   * Initiates upload process.
   */
  protected upload(): void {
    this.disabled.set(true);

    // validate files
    if (!this.files().some(f => isValidFile(f.file, this.fileTypes()))) {
      alert('No valid files selected for upload.');
      this.disabled.set(false);
      return;
    }

    if (this.files().some(f => !isValidFile(f.file, this.fileTypes()))) {
      if (!confirm('Invalid files will be removed during upload. Are you sure you want to continue?')) {
        this.disabled.set(false);
        return;
      }

      // remove invalid files
      this.files.update(files => files.filter(f => isValidFile(f.file, this.fileTypes())));
    }

    this.entity.update(entity => {
      entity!.uploadInfo.name = this.createUploadName();
      entity!.uploadInfo.comment = this.createUploadDescription();
      return entity;
    });

    this.uploadService.create(this.entity()!).pipe(
      tap(entity => this.onSuccessResponse(entity)),
      catchError(error => {
        this.disabled.set(false);
        return of(error);
      })
    ).subscribe();
  }

  /**
   * DataLab API success response handler.
   * 
   * @param entity Data upload.
   */
  private onSuccessResponse(entity: Upload): void {
    console.assert(this.entity() !== entity, '[app-upload-form] onSuccessResponse() same entity');
    this.entity.set(entity);
  }

  /**
   * Creates data upload name.
   * 
   * @returns Data upload name.
   */
  private createUploadName(): string {
    let parts: string[] = [];

    parts.push(
      // study name
      this.selectedStudy()?.name!,
      // account
      this.getTagValue('accountNameId', null),
      // region
      this.getTagValue('regionId', null)
    );

    parts = parts.filter(Boolean);

    if (parts.length > 0) {
      // date
      const now = new Date();
      parts.push(`${now.getFullYear()}${now.getMonth() + 1}${now.getDate()}${now.getHours()}${now.getSeconds()}`);
    }

    return parts
      // apply regex constraint
      .map(p => escape(p, this.entity()!.uploadInfo.namePattern))
      // join parts
      .join('_')
      // apply length constraint
      .substring(0, this.entity()!.uploadInfo.nameMaxLength - 1)
      .trim();
  }

  /**
   * Creates data upload description.
   * 
   * @returns Data upload description.
   */
  private createUploadDescription(): string {
    const parts: string[] = [
      'New',
      this.selectedStudy()?.name!, // study name
      'data received for Account',
      this.getTagValue('accountNameId', 'N/A'), // account
      'in Region',
      this.getTagValue('regionId', 'N/A'), // region
      '. The following information was included Name of surgeon(s) provided are',
      this.getTagValue('surgeonsNameId', 'N/A'), // surgeon(s)
      '. This is an ASC Account:',
      this.getTagValue('isAsc', 'N/A'), // asc
      '. The account has',
      this.getTagValue('datapolicy', 'N/A'), // data policy
      'data policy.'
    ];

    return parts.filter(Boolean).join(' ').substring(0, this.entity()!.uploadInfo.commentMaxLength - 1).trim();
  }

  /**
   * Retrieves pre-defined study tag value.
   * 
   * @param tagName Study tag name.
   * @param defaultValue Default value if tag does not exist or its value is invalid.
   * @returns Tag value.
   */
  private getTagValue(tagName: string, defaultValue: string | null | undefined): string {
    if (this.selectedStudy()?.tags?.length! > 0) {
      const tag = this.selectedStudy()!.tags.find(t => t.name === tagName);
      if (tag?.value)
        return tag.value;
    }

    return defaultValue!;
  }
}
